/**
 * Shared HR list-UI primitives — pure string builders reused across the HR
 * employee, department and position pages (js/views/hr.js). Extracting them here
 * keeps one source of truth for status vocabulary, badge markup, initials
 * avatars and the kebab row-action menu, so the three tables stay visually and
 * behaviourally consistent.
 *
 * `.badge` variants (defined in admin.css): ok=green, off=amber, warn=red,
 * info=blue. `.hr-avatar`, `.hr-menu` / `.hr-menu-wrap` are styled there too.
 */
import { escapeHtml } from './format.js';

/** "on_leave" -> "On leave" */
export function titleCase(s) {
  return String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A `.badge` span. `variant` is one of '', 'ok', 'off', 'warn', 'info'. */
export function badge(label, variant = '', attrs = '') {
  const cls = variant ? `badge ${variant}` : 'badge';
  return `<span class="${cls}"${attrs ? ' ' + attrs : ''}>${escapeHtml(label)}</span>`;
}

/** Up-to-two-letter initials for an avatar chip. */
export function initials(label) {
  const raw = String(label || '').trim();
  if (!raw) return '?';
  const parts = raw.split(/\s+/).filter(Boolean);
  return (parts.length === 1
    ? parts[0].slice(0, 2)
    : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** A round `.hr-avatar` initials chip. Decorative by default (aria-hidden). */
export function initialsAvatar(label, { title = '' } = {}) {
  const t = title ? ` title="${escapeHtml(title)}"` : '';
  return `<span class="hr-avatar" aria-hidden="true"${t}>${escapeHtml(initials(label))}</span>`;
}

/**
 * Single-source employee-status vocabulary. `HrEmployee.status` is free text, so
 * attendance/appraisal features that render a status should read this map too.
 */
export const HR_STATUS_META = {
  active: { label: 'Active', cls: 'ok' },
  on_leave: { label: 'On leave', cls: 'off' },
  probation: { label: 'Probation', cls: 'off' },
  suspended: { label: 'Suspended', cls: 'off' },
  terminated: { label: 'Terminated', cls: 'warn' },
  inactive: { label: 'Inactive', cls: 'warn' },
  left: { label: 'Left', cls: 'warn' },
};

/** Look up the {label, cls} for a free-text status (or a title-cased fallback). */
export function statusMeta(status) {
  const key = String(status || '').toLowerCase();
  return HR_STATUS_META[key] || { label: titleCase(key) || '', cls: '' };
}

/** Coloured pill for a free-text employee status. */
export function statusPill(status) {
  const m = statusMeta(status);
  return badge(m.label || '—', m.cls);
}

/** Green "Active" / red "Inactive" pill from an `is_active` boolean. */
export function activePill(isActive) {
  return isActive === false ? badge('Inactive', 'warn') : badge('Active', 'ok');
}

export const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract' };

/** Badge for an EmploymentType enum value. */
export function employmentBadge(type) {
  const key = String(type || '').toLowerCase();
  if (!key) return '<span class="muted">—</span>';
  return badge(EMPLOYMENT_LABELS[key] || titleCase(key));
}

/**
 * A kebab (⋮) row-action menu cell. `ns` namespaces the data-attributes so each
 * page keeps its own click-handler selectors:
 *   data-${ns}-menu / data-${ns}-menu-for / data-${ns}-action / data-${ns}-target
 * `actions` is an array of `{ action, label }`. Returns a full `<td>`.
 */
export function kebabMenu({ id, ns, actions = [], label = 'Actions' }) {
  const sid = escapeHtml(String(id ?? ''));
  const items = actions.map((a) =>
    `<button type="button" role="menuitem" data-${ns}-action="${escapeHtml(a.action)}" data-${ns}-target="${sid}">${escapeHtml(a.label)}</button>`
  ).join('');
  return `<td class="hr-menu-wrap">`
    + `<button type="button" class="btn ghost" data-${ns}-menu="${sid}" aria-haspopup="menu" aria-expanded="false" title="${escapeHtml(label)}">⋮</button>`
    + `<div class="hr-menu" data-${ns}-menu-for="${sid}" role="menu" hidden>${items}</div>`
    + `</td>`;
}
